import { ConversationMessageViewModel } from '@@api';

import { ConversationParticipantViewModel } from '../../api/api';
import { getChatAttribute, mergeChatAttributes } from '../../components/Chat/ChatMessageAttributes';
import { ChatMessage } from '../../components/Chat/PromptChatAdapter';

/** Adapt stored content attributes to the same progress rendering used by PromptChat. */
export function toConversationChatMessage(message: ConversationMessageViewModel, participants: ConversationParticipantViewModel[] = []): ChatMessage {
	const participant = participants.find((item) => item.id === message.participantId);
	const attributes = message.contents.reduce<Record<string, string> | undefined>(
		(merged, content) => mergeChatAttributes(merged, content.attributes),
		undefined,
	);
	return {
		id: message.id,
		groupId: message.groupId,
		createdDate: message.createdDate,
		role: getChatAttribute(attributes, 'sk:role', 'role') ?? (participant?.type.toLowerCase() === 'user' ? 'user' : 'assistant'),
		agentName: getChatAttribute(attributes, 'progress:agentName', 'sk:agentName', 'agentName') ?? participant?.name,
		contentType: getChatAttribute(attributes, 'ContentType', 'sk:contentType') ?? 'markdown',
		text: message.contents
			.map((content) => content.text)
			.filter(Boolean)
			.join('\n\n'),
		attributes,
	};
}

export type ConversationMessageGroup = {
	key: string;
	groupId?: string;
	messages: ConversationMessageViewModel[];
};

export function prependConversationMessages(
	olderMessages: ConversationMessageViewModel[],
	currentMessages: ConversationMessageViewModel[],
): ConversationMessageViewModel[] {
	const currentMessageIds = new Set(currentMessages.map((message) => message.id));
	return [...olderMessages.filter((message) => !currentMessageIds.has(message.id)), ...currentMessages];
}

export function groupConversationMessages(messages: ConversationMessageViewModel[]): ConversationMessageGroup[] {
	const groups: ConversationMessageGroup[] = [];

	for (const message of messages) {
		const previousGroup = groups.at(-1);
		if (message.groupId && previousGroup?.groupId === message.groupId) {
			previousGroup.messages.push(message);
			continue;
		}

		groups.push({
			key: message.groupId ?? message.id,
			groupId: message.groupId,
			messages: [message],
		});
	}

	return groups;
}
